import { canonicalizeJson } from "./canonical-json";

export const MAX_LUMINAIRE_JSON_BYTES = 2 * 1024 * 1024;
export const MAX_LUMINAIRE_JSON_DEPTH = 128;

export class LuminaireJsonError extends Error {
  constructor() { super("Expected bounded, unambiguous UTF-8 JSON with unique object keys."); this.name = "LuminaireJsonError"; }
}

/** Parse I-JSON input without silently discarding duplicate (including escaped) keys. */
export function parseLuminaireJson(text: string): unknown {
  try {
    if (typeof text !== "string" || !text.length || new TextEncoder().encode(text).byteLength > MAX_LUMINAIRE_JSON_BYTES) throw new Error();
    const input: unknown = JSON.parse(text);
    type Frame = { kind: "array" } | { kind: "object"; keys: Set<string>; expectsKey: boolean };
    const frames: Frame[] = [];
    for (let index = 0; index < text.length; index++) {
      const character = text[index];
      if (character === '"') {
        const start = index;
        for (index++; index < text.length; index++) {
          if (text[index] === "\\") index++;
          else if (text[index] === '"') break;
        }
        const frame = frames[frames.length - 1];
        if (frame?.kind === "object" && frame.expectsKey) {
          const key = JSON.parse(text.slice(start, index + 1)) as string;
          if (frame.keys.has(key)) throw new Error();
          frame.keys.add(key);
          frame.expectsKey = false;
        }
      } else if (character === "{" || character === "[") {
        if (frames.length >= MAX_LUMINAIRE_JSON_DEPTH) throw new Error();
        frames.push(character === "{" ? { kind: "object", keys: new Set(), expectsKey: true } : { kind: "array" });
      } else if (character === "}" || character === "]") {
        frames.pop();
      } else if (character === ",") {
        const frame = frames[frames.length - 1];
        if (frame?.kind === "object") frame.expectsKey = true;
      }
    }
    // Reject non-finite numeric results and lone surrogate strings as required
    // by the same RFC8785 representation used for draft/revision digests.
    canonicalizeJson(input);
    return input;
  } catch { throw new LuminaireJsonError(); }
}
